import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useRef, useState } from 'react';

export const usePromptOverflow = (
  editor: Editor | null,
  expanded: boolean,
  draftKey: unknown,
  enabled: boolean
): boolean => {
  const [hasPromptOverflow, setHasPromptOverflow] = useState(false);
  const frameRef = useRef<number | null>(null);
  const measure = useCallback(() => {
    if (!enabled || !editor || expanded) return;
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    // RO + resize + draft can fire together; measure on the next frame.
    // +1 ignores sub-pixel / scrollbar rounding so a flush wrap doesn't show the toggle.
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const element = editor.view.dom;
      const next = element.scrollHeight > element.clientHeight + 1;
      setHasPromptOverflow((current) => (current === next ? current : next));
    });
  }, [editor, enabled, expanded]);

  useEffect(() => {
    measure();
  }, [draftKey, measure]);

  useEffect(() => {
    if (!enabled || !editor || expanded) return;
    const element = editor.view.dom;
    const resizeObserver =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    resizeObserver?.observe(element);
    window.addEventListener('resize', measure);
    measure();
    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measure);
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [editor, enabled, expanded, measure]);

  return hasPromptOverflow;
};
